import { useEffect, useMemo, useState } from 'react';
import {
  Box, Card, CardContent, Stack, Typography, Avatar, Chip,
  Button, IconButton, Tooltip, CircularProgress, Alert,
  Table, TableHead, TableRow, TableCell, TableBody, TableContainer,
  TablePagination, TextField, InputAdornment,
  ToggleButton, ToggleButtonGroup,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import {
  Description as VoucherIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Search as SearchIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  AccountBalance as BankIcon,
  Person as PersonIcon,
} from '@mui/icons-material';
import ModuleHeader from '../../components/ModuleHeader';
import { useIsMobile, MobileDataCard } from '../../components/ResponsiveTable';
import API from '../../BaseURL';
import swal from '../../utils/swal';

const TNUM = { fontVariantNumeric: 'tabular-nums' };
const fmtMoney = (n) =>
  Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const fmtDate = (d) => {
  if (!d) return '—';
  const dt = new Date(d);
  return isNaN(dt.getTime()) ? String(d) : dt.toLocaleDateString();
};

// UI-side label for a voucher row. The DB stores "POSTED" for approved
// payments, but for this page we want the approver-friendly word
// "APPROVED" because that's the action the approver took.
const uiStatus = (dbStatus) => (dbStatus === 'POSTED' ? 'APPROVED' : dbStatus);

export default function CashbookApprovalsPage() {
  const isMobile = useIsMobile();
  const [vouchers, setVouchers] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [busy,    setBusy]      = useState({});  // { [id]: boolean }
  const [q,       setQ]         = useState('');
  const [page,    setPage]      = useState(0);
  const [rpp,     setRpp]       = useState(10);
  // Status filter: ALL | PENDING | APPROVED | REJECTED
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [from, setFrom] = useState('');
  const [to,   setTo]   = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const r = await API.get('/cashbook/payment-approvals', {
        params: {
          status: statusFilter !== 'ALL' ? statusFilter : undefined,
          from: from || undefined,
          to:   to   || undefined,
        },
      });
      setVouchers(r.data || []);
    } catch (e) {
      swal.error(swal.pickError(e, 'Could not load payment approvals'));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [statusFilter, from, to]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    if (!term) return vouchers;
    return vouchers.filter(v => {
      const haystack = [
        v.voucher_no, v.acc_name, v.exp_cat, v.exp_ty, v.narration,
        v.cheque_no, v.request_holder,
        v.created_by_name, v.approved_by_name, v.reversed_by_name,
        v.reversal_reason, v.status,
        ...((v.items || []).flatMap(it => [it.exp_cat, it.exp_ty, it.narration, it.request_holder])),
      ].filter(Boolean).join(' ').toLowerCase();
      return haystack.includes(term);
    });
  }, [vouchers, q]);

  // Counts shown in the toggle chips. Cashbook DB stores POSTED for
  // approved vouchers, so collapse it under APPROVED for the UI.
  const counts = useMemo(() => {
    const c = { ALL: vouchers.length, PENDING: 0, APPROVED: 0, REJECTED: 0 };
    vouchers.forEach(v => {
      const k = uiStatus(v.status);
      if (c[k] !== undefined) c[k]++;
    });
    return c;
  }, [vouchers]);

  const approve = async (v) => {
    const ok = await swal.confirm({
      title: `Approve ${v.voucher_no}?`,
      text: `Posting GHS ${fmtMoney(v.amount)} from ${v.acc_name} for ${(v.items || []).length || 1} item(s). This writes to the cashbook and journal.`,
      tone: 'success', confirmText: 'Yes, approve & post',
    });
    if (!ok) return;
    setBusy(b => ({ ...b, [v.id]: true }));
    try {
      await API.post(`/cashbook/payments/${v.id}/approve`);
      swal.success(`${v.voucher_no} posted`);
      window.dispatchEvent(new Event('pendingCountsChanged'));
      await load();
    } catch (e) {
      swal.error(swal.pickError(e, 'Approval failed'));
    } finally { setBusy(b => ({ ...b, [v.id]: false })); }
  };

  const reject = async (v) => {
    const reason = await swal.prompt({
      title: `Reject ${v.voucher_no}?`,
      text: `${v.acc_name} · GHS ${fmtMoney(v.amount)} · ${v.request_holder || ''}`,
      inputLabel: 'Reason for rejection',
      inputType: 'textarea',
      confirmText: 'Reject',
      validate: (val) => (!val || val.trim().length < 5) ? 'Reason ≥ 5 chars' : null,
    });
    if (!reason) return;
    setBusy(b => ({ ...b, [v.id]: true }));
    try {
      await API.post(`/cashbook/payments/${v.id}/reject`, { reason });
      swal.success(`${v.voucher_no} rejected`);
      window.dispatchEvent(new Event('pendingCountsChanged'));
      await load();
    } catch (e) {
      swal.error(swal.pickError(e, 'Reject failed'));
    } finally { setBusy(b => ({ ...b, [v.id]: false })); }
  };

  const toneFor = (status) =>
    status === 'PENDING'  ? 'warning'
  : status === 'APPROVED' ? 'success'
  : status === 'REJECTED' ? 'error'
  :                         'primary';

  return (
    <Box sx={{ maxWidth: 1500, mx: 'auto', pb: 4 }}>
      <ModuleHeader
        icon={<VoucherIcon />}
        title="Cashbook · Payment Approvals"
        subtitle="Review, approve and audit Payment Vouchers before they hit the cashbook and journal"
        actionLabel="Refresh"
        actionIcon={<RefreshIcon />}
        onAction={load}
      />

      {/* Status + date filters */}
      <Card sx={{ mb: 2, borderRadius: 2 }}>
        <Box sx={{ p: 1.5, display: 'flex', alignItems: 'center', gap: 1.2, flexWrap: 'wrap' }}>
          <ToggleButtonGroup exclusive size="small"
            value={statusFilter}
            onChange={(_, v) => { if (v) { setStatusFilter(v); setPage(0); } }}
            sx={{
              '& .MuiToggleButton-root': {
                textTransform: 'none', fontWeight: 700, px: 1.6, py: 0.6, fontSize: 12,
                '&.Mui-selected': { fontWeight: 800 },
              },
            }}>
            <ToggleButton value="ALL">All
              <Chip size="small" variant="outlined"
                label={counts.ALL}
                sx={{ ml: 0.8, height: 18, fontSize: 10, fontWeight: 800,
                  '& .MuiChip-label': { px: 0.7 } }} />
            </ToggleButton>
            <ToggleButton value="PENDING">Pending
              <Chip size="small" color="warning"
                label={counts.PENDING}
                sx={{ ml: 0.8, height: 18, fontSize: 10, fontWeight: 800,
                  '& .MuiChip-label': { px: 0.7 } }} />
            </ToggleButton>
            <ToggleButton value="APPROVED">Approved
              <Chip size="small" color="success"
                label={counts.APPROVED}
                sx={{ ml: 0.8, height: 18, fontSize: 10, fontWeight: 800,
                  '& .MuiChip-label': { px: 0.7 } }} />
            </ToggleButton>
            <ToggleButton value="REJECTED">Rejected
              <Chip size="small" color="error"
                label={counts.REJECTED}
                sx={{ ml: 0.8, height: 18, fontSize: 10, fontWeight: 800,
                  '& .MuiChip-label': { px: 0.7 } }} />
            </ToggleButton>
          </ToggleButtonGroup>
          <Box sx={{ flex: 1 }} />
          <TextField size="small" type="date" label="From"
            InputLabelProps={{ shrink: true }}
            value={from} onChange={(e) => setFrom(e.target.value)}
            sx={{ width: 160 }} />
          <TextField size="small" type="date" label="To"
            InputLabelProps={{ shrink: true }}
            value={to} onChange={(e) => setTo(e.target.value)}
            sx={{ width: 160 }} />
          {(from || to) && (
            <Button size="small" onClick={() => { setFrom(''); setTo(''); }}
              startIcon={<CloseIcon fontSize="small" />}
              sx={{ textTransform: 'none', fontWeight: 700 }}>
              Clear dates
            </Button>
          )}
        </Box>
      </Card>

      {/* Summary strip */}
      {!loading && vouchers.length > 0 && (
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', md: 'repeat(3, 1fr)' },
          gap: 1.5, mb: 2.5,
        }}>
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid', borderColor: `${toneFor(statusFilter)}.main` }}>
            <Box sx={{ p: 1.6, display: 'flex', alignItems: 'center', gap: 1.6 }}>
              <Avatar sx={{ width: 42, height: 42,
                bgcolor: (t) => alpha(t.palette[toneFor(statusFilter)].main, 0.14),
                color: `${toneFor(statusFilter)}.main` }}>
                <VoucherIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10, display: 'block' }}>
                  {statusFilter === 'ALL' ? 'Total Vouchers' : `${statusFilter[0]}${statusFilter.slice(1).toLowerCase()} Vouchers`}
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1.15, ...TNUM }}>
                  {vouchers.length}
                </Typography>
              </Box>
            </Box>
          </Card>
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid', borderColor: 'primary.main' }}>
            <Box sx={{ p: 1.6, display: 'flex', alignItems: 'center', gap: 1.6 }}>
              <Avatar sx={{ width: 42, height: 42, bgcolor: alpha('#1565C0', 0.14), color: 'primary.main' }}>
                <BankIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10, display: 'block' }}>
                  Total Value
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1.15, ...TNUM }}>
                  GHS {fmtMoney(vouchers.reduce((s, v) => s + Number(v.amount || 0), 0))}
                </Typography>
              </Box>
            </Box>
          </Card>
          <Card sx={{ borderRadius: 2, borderLeft: '4px solid', borderColor: 'success.main' }}>
            <Box sx={{ p: 1.6, display: 'flex', alignItems: 'center', gap: 1.6 }}>
              <Avatar sx={{ width: 42, height: 42, bgcolor: alpha('#2e7d32', 0.14), color: 'success.main' }}>
                <ApproveIcon />
              </Avatar>
              <Box>
                <Typography variant="caption" color="text.secondary"
                  sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 800, fontSize: 10, display: 'block' }}>
                  Average Voucher
                </Typography>
                <Typography sx={{ fontWeight: 800, fontSize: 22, lineHeight: 1.15, ...TNUM }}>
                  GHS {fmtMoney(vouchers.reduce((s, v) => s + Number(v.amount || 0), 0) / Math.max(1, vouchers.length))}
                </Typography>
              </Box>
            </Box>
          </Card>
        </Box>
      )}

      {!loading && vouchers.length === 0 && (
        <Alert
          severity={statusFilter === 'PENDING' ? 'success' : 'info'}
          variant="outlined" sx={{ borderRadius: 2 }}>
          {statusFilter === 'PENDING'
            ? 'The approval queue is empty — no pending Payment Vouchers right now.'
            : `No ${statusFilter.toLowerCase()} payment vouchers${from || to ? ' in this date range' : ''}.`}
        </Alert>
      )}

      {loading && (
        <Card sx={{ p: 6, textAlign: 'center', mb: 2 }}><CircularProgress /></Card>
      )}

      {!loading && vouchers.length > 0 && (
        <Card sx={{ borderRadius: 2, overflow: 'hidden',
          border: '1px solid',
          borderColor: (t) => alpha(t.palette[toneFor(statusFilter)].main, 0.3) }}>
          <Box sx={{
            px: 2.5, py: 1.4,
            background: (t) => {
              const c = t.palette[toneFor(statusFilter)].main;
              return `linear-gradient(135deg, ${alpha(c, 0.10)} 0%, ${alpha(c, 0.02)} 100%)`;
            },
            borderBottom: 1, borderColor: 'divider',
            display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap',
          }}>
            <Avatar sx={{ width: 30, height: 30, bgcolor: `${toneFor(statusFilter)}.main`, fontSize: 14 }}>
              <VoucherIcon fontSize="small" />
            </Avatar>
            <Box sx={{ flex: 1, minWidth: 200 }}>
              <Typography variant="subtitle2" fontWeight={800} sx={{ lineHeight: 1.1 }}>
                {statusFilter === 'ALL' ? 'All Payment Vouchers'
                : statusFilter === 'PENDING'  ? 'Pending Payment Vouchers'
                : statusFilter === 'APPROVED' ? 'Approved Payment Vouchers'
                :                                'Rejected Payment Vouchers'}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {statusFilter === 'PENDING'
                  ? 'Approve to post to cashbook + journal · Reject to send back'
                  : 'Historical record · who decided, when, and why'}
              </Typography>
            </Box>
            <Chip size="small" variant="outlined"
              label={`${filtered.length}`}
              sx={{ fontWeight: 700, bgcolor: 'background.paper' }} />
            <TextField size="small" placeholder="Search voucher, account, item, requester, approver…"
              value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }}
              sx={{ minWidth: 280, bgcolor: 'background.paper', borderRadius: 1 }}
              InputProps={{
                startAdornment: <InputAdornment position="start"><SearchIcon fontSize="small" /></InputAdornment>,
                endAdornment: q && (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => setQ('')}>
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </InputAdornment>
                ),
              }} />
          </Box>

          {isMobile ? (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, p: 1 }}>
              {filtered.slice(page * rpp, (page + 1) * rpp).map(v => (
                <VoucherMobileCard key={v.id} v={v}
                  busy={!!busy[v.id]}
                  onApprove={() => approve(v)}
                  onReject={() => reject(v)} />
              ))}
            </Box>
          ) : (
            <TableContainer sx={{ maxHeight: 640 }}>
              <Table stickyHeader size="small" sx={{
                '& .MuiTableCell-head': {
                  bgcolor: '#fafbfc', fontWeight: 800, fontSize: 10.5,
                  textTransform: 'uppercase', letterSpacing: 0.6,
                  color: 'text.secondary',
                  borderBottom: '2px solid',
                  borderColor: `${toneFor(statusFilter)}.light`,
                  py: 1.1,
                },
              }}>
                <TableHead>
                  <TableRow>
                    <TableCell>Voucher No.</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>From Account</TableCell>
                    <TableCell>Cheque</TableCell>
                    <TableCell>Items</TableCell>
                    <TableCell sx={{ minWidth: 220 }}>Status / Decision</TableCell>
                    <TableCell align="right">Total</TableCell>
                    <TableCell align="right" sx={{ width: 200 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filtered
                    .slice(page * rpp, (page + 1) * rpp)
                    .map(v => (
                      <VoucherRow key={v.id} v={v}
                        busy={!!busy[v.id]}
                        onApprove={() => approve(v)}
                        onReject={() => reject(v)} />
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          <TablePagination
            component="div"
            count={filtered.length}
            page={page}
            onPageChange={(_, p) => setPage(p)}
            rowsPerPage={rpp}
            onRowsPerPageChange={(e) => { setRpp(Number(e.target.value)); setPage(0); }}
            rowsPerPageOptions={[5, 10, 25, 50]}
            sx={{ borderTop: 1, borderColor: 'divider', bgcolor: 'background.paper' }}
          />
        </Card>
      )}
    </Box>
  );
}

function VoucherRow({ v, busy, onApprove, onReject }) {
  const [open, setOpen] = useState(false);
  const items = v.items || [];
  const ui = uiStatus(v.status);
  const isPending  = ui === 'PENDING';
  const isApproved = ui === 'APPROVED';
  const isRejected = ui === 'REJECTED';
  // For rejected vouchers the rejecter is recorded in `reversed_by`
  // (and reason in `reversal_reason`); for approved vouchers the
  // approver is in `approved_by` / `approved_at`.
  const decisionName =
    isApproved ? (v.approved_by_name || '—')
  : isRejected ? (v.reversed_by_name || '—')
  :              null;
  const decisionDate =
    isApproved ? v.approved_at
  : isRejected ? v.reversed_at
  :              null;

  return (
    <>
      <TableRow hover sx={{
        '& td': { fontSize: 12, py: 1, ...TNUM,
          borderBottom: '1px solid', borderColor: 'rgba(0,0,0,0.04)' },
        bgcolor:
          isApproved ? alpha('#2e7d32', 0.03)
        : isRejected ? alpha('#d32f2f', 0.03)
        : '#ffffff',
      }}>
        <TableCell sx={{ fontWeight: 700, fontFamily: 'monospace', fontSize: 12 }}>
          {v.voucher_no}
        </TableCell>
        <TableCell sx={{ color: 'text.secondary' }}>{fmtDate(v.pay_date)}</TableCell>
        <TableCell sx={{ fontWeight: 700 }}>
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <BankIcon fontSize="small" sx={{ color: 'primary.main', fontSize: 14 }} />
            <span>{v.acc_name}</span>
          </Stack>
        </TableCell>
        <TableCell sx={{ fontFamily: 'monospace', fontSize: 11.5 }}>{v.cheque_no || '—'}</TableCell>
        <TableCell>
          <Button size="small" variant="text" onClick={() => setOpen(o => !o)}
            sx={{ textTransform: 'none', fontWeight: 700, fontSize: 12, p: 0, minWidth: 0 }}>
            {items.length} {items.length === 1 ? 'item' : 'items'} {open ? '▾' : '▸'}
          </Button>
        </TableCell>
        <TableCell sx={{ minWidth: 220 }}>
          <Stack direction="column" spacing={0.3}>
            <Chip size="small" sx={{ width: 'fit-content', height: 19, fontSize: 10, fontWeight: 800 }}
              color={
                isPending  ? 'warning'
              : isApproved ? 'success'
              : isRejected ? 'error'
              :              'default'
              }
              label={ui} />
            {(isApproved || isRejected) && (
              <Tooltip title={isRejected ? (v.reversal_reason || '') : ''}>
                <Typography variant="caption" sx={{
                  fontSize: 11, color: 'text.secondary',
                  display: 'block', whiteSpace: 'nowrap',
                  overflow: 'hidden', textOverflow: 'ellipsis',
                  maxWidth: 220,
                }}>
                  {isApproved ? 'Approved' : 'Rejected'} by{' '}
                  <strong>{decisionName}</strong>
                  {decisionDate && ` · ${fmtDate(decisionDate)}`}
                  {isRejected && v.reversal_reason && (
                    <> · <span style={{ fontStyle: 'italic' }}>{v.reversal_reason}</span></>
                  )}
                </Typography>
              </Tooltip>
            )}
            {isPending && v.created_by_name && (
              <Typography variant="caption" sx={{ fontSize: 11, color: 'text.secondary' }}>
                Prepared by <strong>{v.created_by_name}</strong>
              </Typography>
            )}
          </Stack>
        </TableCell>
        <TableCell align="right" sx={{ fontWeight: 800,
          color: isApproved ? 'success.dark' : isRejected ? 'error.main' : 'error.main' }}>
          GHS {fmtMoney(v.amount)}
        </TableCell>
        <TableCell align="right">
          {isPending ? (
            <Stack direction="row" spacing={0.7} justifyContent="flex-end">
              <Button size="small" variant="contained" color="success"
                startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <ApproveIcon fontSize="small" />}
                onClick={onApprove} disabled={busy}
                sx={{ textTransform: 'none', fontWeight: 800 }}>
                Approve
              </Button>
              <Button size="small" variant="outlined" color="error"
                startIcon={<RejectIcon fontSize="small" />}
                onClick={onReject} disabled={busy}
                sx={{ textTransform: 'none', fontWeight: 800 }}>
                Reject
              </Button>
            </Stack>
          ) : (
            <Typography variant="caption" color="text.disabled" sx={{ fontStyle: 'italic' }}>
              No actions
            </Typography>
          )}
        </TableCell>
      </TableRow>
      {open && (
        <TableRow>
          <TableCell colSpan={8} sx={{ bgcolor: '#fafbfc', py: 1.3 }}>
            <Typography variant="caption" fontWeight={800}
              sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 10, color: 'text.secondary', display: 'block', mb: 0.7 }}>
              Items covered by this cheque
            </Typography>
            <Box sx={{ display: 'grid', gap: 0.6 }}>
              {items.map((it, i) => (
                <Box key={i} sx={{
                  display: 'grid',
                  gridTemplateColumns: { xs: '1fr', md: '1.2fr 1.2fr 0.8fr 1.2fr 1fr' },
                  gap: 1, px: 1.2, py: 0.7,
                  bgcolor: 'background.paper',
                  border: '1px solid', borderColor: 'divider', borderRadius: 1,
                  fontSize: 12, ...TNUM,
                }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 9.5, fontWeight: 800, display: 'block' }}>
                      Category
                    </Typography>
                    <Typography fontWeight={700} fontSize={12}>{it.exp_cat}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 9.5, fontWeight: 800, display: 'block' }}>
                      Type
                    </Typography>
                    <Typography fontWeight={700} fontSize={12}>{it.exp_ty}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 9.5, fontWeight: 800, display: 'block' }}>
                      Amount
                    </Typography>
                    <Typography fontWeight={800} fontSize={12} sx={{ color: 'error.main' }}>
                      GHS {fmtMoney(it.amount)}
                    </Typography>
                  </Box>
                  <Box>
                    <Stack direction="row" alignItems="center" spacing={0.4}>
                      <PersonIcon fontSize="small" sx={{ fontSize: 12, color: 'text.disabled' }} />
                      <Typography variant="caption" color="text.secondary"
                        sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 9.5, fontWeight: 800 }}>
                        Requester
                      </Typography>
                    </Stack>
                    <Typography fontWeight={700} fontSize={12}>{it.request_holder || '—'}</Typography>
                  </Box>
                  <Box>
                    <Typography variant="caption" color="text.secondary"
                      sx={{ textTransform: 'uppercase', letterSpacing: 0.5, fontSize: 9.5, fontWeight: 800, display: 'block' }}>
                      Narration
                    </Typography>
                    <Typography fontSize={12}>{it.narration || '—'}</Typography>
                  </Box>
                </Box>
              ))}
            </Box>
            {v.narration && (
              <Box sx={{ mt: 1, p: 1, bgcolor: 'background.paper', border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
                <Typography variant="caption" color="text.secondary" fontWeight={800} sx={{ display: 'block' }}>
                  Cheque-level narration
                </Typography>
                <Typography variant="body2">{v.narration}</Typography>
              </Box>
            )}
          </TableCell>
        </TableRow>
      )}
    </>
  );
}

// Phone-sized card view of a voucher row. Mirrors the desktop columns
// but stacks them vertically with a tap-target action row at the bottom.
function VoucherMobileCard({ v, busy, onApprove, onReject }) {
  const [open, setOpen] = useState(false);
  const items = v.items || [];
  const ui = uiStatus(v.status);
  const isPending  = ui === 'PENDING';
  const isApproved = ui === 'APPROVED';
  const isRejected = ui === 'REJECTED';
  const decisionName =
    isApproved ? (v.approved_by_name || '—')
  : isRejected ? (v.reversed_by_name || '—')
  :              null;
  const decisionDate =
    isApproved ? v.approved_at
  : isRejected ? v.reversed_at
  :              null;

  const accent =
    isPending  ? '#ed6c02'
  : isApproved ? '#2e7d32'
  : isRejected ? '#d32f2f'
  :              '#1565C0';

  return (
    <MobileDataCard
      accent={accent}
      title={v.voucher_no}
      subtitle={`${fmtDate(v.pay_date)} · ${v.acc_name}`}
      status={ui}
      statusColor={
        isPending  ? 'warning'
      : isApproved ? 'success'
      : isRejected ? 'error'
      :              'default'
      }
      fields={[
        { label: 'Cheque',  value: v.cheque_no || '—' },
        { label: 'Items',   value: (
          <Button size="small" variant="text" onClick={() => setOpen(o => !o)}
            sx={{ p: 0, minWidth: 0, fontSize: 12, fontWeight: 700, textTransform: 'none' }}>
            {items.length} {items.length === 1 ? 'item' : 'items'} {open ? '▾' : '▸'}
          </Button>
        ) },
        ...(isPending && v.created_by_name ? [{
          label: 'Prepared', value: v.created_by_name, bold: true,
        }] : []),
        ...((isApproved || isRejected) ? [{
          label: isApproved ? 'Approved' : 'Rejected',
          value: (
            <Box>
              <Typography fontSize={12} fontWeight={700}>{decisionName}</Typography>
              {decisionDate && (
                <Typography fontSize={11} color="text.secondary">
                  {fmtDate(decisionDate)}
                </Typography>
              )}
              {isRejected && v.reversal_reason && (
                <Typography fontSize={11} color="text.secondary"
                  sx={{ fontStyle: 'italic', mt: 0.3 }}>
                  {v.reversal_reason}
                </Typography>
              )}
            </Box>
          ),
        }] : []),
      ]}
      amount={`GHS ${fmtMoney(v.amount)}`}
      amountColor={isApproved ? 'success.dark' : 'error.main'}
      amountLabel="Total"
      footer={open && (
        <Box sx={{ display: 'grid', gap: 0.6 }}>
          <Typography variant="caption" fontWeight={800}
            sx={{ textTransform: 'uppercase', letterSpacing: 0.6, fontSize: 9.5, color: 'text.secondary' }}>
            Items
          </Typography>
          {items.map((it, i) => (
            <Box key={i} sx={{
              p: 0.8, borderRadius: 1, bgcolor: '#fafbfc',
              border: '1px solid', borderColor: 'divider',
            }}>
              <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                <Typography fontSize={12} fontWeight={700}>{it.exp_cat}</Typography>
                <Typography fontSize={12} fontWeight={800} sx={{ color: 'error.main', ...TNUM }}>
                  GHS {fmtMoney(it.amount)}
                </Typography>
              </Stack>
              <Typography fontSize={11} color="text.secondary">{it.exp_ty}</Typography>
              {it.request_holder && (
                <Stack direction="row" alignItems="center" spacing={0.4} sx={{ mt: 0.3 }}>
                  <PersonIcon sx={{ fontSize: 11, color: 'text.disabled' }} />
                  <Typography fontSize={11}>{it.request_holder}</Typography>
                </Stack>
              )}
              {it.narration && (
                <Typography fontSize={11} sx={{ mt: 0.3, fontStyle: 'italic' }}>
                  {it.narration}
                </Typography>
              )}
            </Box>
          ))}
          {v.narration && (
            <Box sx={{ mt: 0.4, p: 0.8, border: '1px dashed', borderColor: 'divider', borderRadius: 1 }}>
              <Typography fontSize={9.5} fontWeight={800} color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
                Cheque narration
              </Typography>
              <Typography fontSize={12}>{v.narration}</Typography>
            </Box>
          )}
        </Box>
      )}
      actions={isPending ? (
        <>
          <Button size="small" variant="contained" color="success"
            startIcon={busy ? <CircularProgress size={14} color="inherit" /> : <ApproveIcon fontSize="small" />}
            onClick={onApprove} disabled={busy}
            sx={{ textTransform: 'none', fontWeight: 800, flex: 1 }}>
            Approve
          </Button>
          <Button size="small" variant="outlined" color="error"
            startIcon={<RejectIcon fontSize="small" />}
            onClick={onReject} disabled={busy}
            sx={{ textTransform: 'none', fontWeight: 800, flex: 1 }}>
            Reject
          </Button>
        </>
      ) : null}
    />
  );
}
